import { clearAccessToken, getAccessToken, setAccessToken } from "./authToken";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export class ApiError extends Error {
  status: number;
  data?: unknown;
  detail?: unknown;

  constructor(message: string, status: number, data?: unknown, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.detail = detail;
  }
}

export type ApiRequestOptions = {
  method?: HttpMethod;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
  authenticated?: boolean;
};

const DEFAULT_HEADERS: Record<string, string> = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:8080";

const buildUrl = (path: string): string => {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }

  const trimmedPath = path.startsWith("/") ? path : `/${path}`;
  return `${API_BASE_URL}${trimmedPath}`;
};

export async function apiRequest<TResponse>(
  path: string,
  options: ApiRequestOptions = {}
): Promise<TResponse> {
  const {
    method = "GET",
    body,
    headers,
    signal,
    authenticated = true,
  } = options;

  const resolvedHeaders: Record<string, string> = {
    ...DEFAULT_HEADERS,
    ...headers,
  };

  const authToken = authenticated ? getAccessToken() : null;
  if (authenticated && authToken) {
    resolvedHeaders.Authorization = `Bearer ${authToken}`;
  }

  let requestBody: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof URLSearchParams || body instanceof Blob) {
      requestBody = body;
      if (!(body instanceof Blob)) {
        delete resolvedHeaders["Content-Type"];
      }
    } else if (typeof body === "string") {
      requestBody = body;
      resolvedHeaders["Content-Type"] = resolvedHeaders["Content-Type"] ?? "text/plain";
    } else {
      requestBody = JSON.stringify(body);
    }
  }

  const response = await fetch(buildUrl(path), {
    method,
    headers: resolvedHeaders,
    body: requestBody,
    signal,
  });

  const refreshedToken = response.headers.get("x-refresh-token");
  if (refreshedToken) {
    setAccessToken(refreshedToken);
  }

  if (!response.ok) {
    const contentType = response.headers.get("content-type");
    let payload: unknown = null;

    if (contentType?.includes("application/json")) {
      payload = await response.json().catch(() => null);
    } else {
      const errorText = await response.text().catch(() => "");
      payload = errorText || null;
    }

    const detail =
      payload && typeof payload === "object" && "detail" in payload
        ? (payload as Record<string, unknown>).detail
        : undefined;

    const detailMessage =
      typeof detail === "string"
        ? detail
        : detail && typeof detail === "object" && detail !== null && "message" in detail
          ? String((detail as Record<string, unknown>).message)
          : undefined;

    const rootMessage =
      payload && typeof payload === "object" && "message" in payload
        ? String((payload as Record<string, unknown>).message)
        : undefined;

    const fallbackMessage =
      typeof payload === "string" && payload.length > 0
        ? payload
        : `Request to ${path} failed with status ${response.status}`;

    const message = detailMessage ?? rootMessage ?? fallbackMessage;
    if (response.status === 401) {
      clearAccessToken();
    }
    throw new ApiError(message, response.status, payload, detail);
  }

  if (response.status === 204) {
    return undefined as unknown as TResponse;
  }

  const contentType = response.headers.get("content-type");
  if (contentType?.includes("application/json")) {
    return (await response.json()) as TResponse;
  }

  const text = await response.text();
  return text as unknown as TResponse;
}
